import type { Dispatch, MouseEvent, SetStateAction } from 'react'
import { useCallback, useState } from 'react'
import { PostalCodePlusIcon } from 'src/components/Icons/PostalCodePlusIcon'
import type { Address } from 'src/components/checkout/steps/shipping/types'
import type { AddressData } from 'src/sdk/checkout/data/types'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { checkHyphenInCEP } from 'src/components/checkout/steps/shipping/hook/useAddressCheckout'
import type { AddressSchemaType } from 'src/components/checkout/steps/shipping/components/Address/AddressForm'

import { PostalCodeModalHeader } from './PostalCodeModalHeader'
import { PostalCodeAddressList } from '../Address/PostalCodeAddressList'
import { SectionDivider } from '../Input/SectionDivider'
import { PostalCodeInputForm } from '../Input/PostalCodeInputForm'
import { AddressFormModal } from '../Address/AddressFormModal'
import { syncContextAddressFromAddressData } from '../../utils/syncContextAddressFromAddressData'

const CORREIOS_CEP_FINDER_HREF =
  'https://buscacepinter.correios.com.br/app/endereco/index.php'

interface PostalCodeModalContentProps {
  onModalClose: (args: {
    shouldTrackClose: boolean
    hasInputValue?: boolean
    buttonName?: string
    skipTracking?: boolean
  }) => void
  onSubmit: (
    value: string,
    options?: { shouldTrackConfirmation?: boolean }
  ) => Promise<void>
  inputValue: string
  setInputValue: Dispatch<SetStateAction<string>>
  address: AddressData | null
  setProvisionalAddress: Dispatch<SetStateAction<Address | null>>
  defaultAddressId: string | null
  setDefaultAddressId: Dispatch<SetStateAction<string | null>>
  setPostalCode: (postalCode: string) => void
  setAddress: Dispatch<SetStateAction<Address>>
}

function buildAddressFormDraftRemountKey(
  provisional: AddressData | null
): string {
  if (!provisional) {
    return 'empty'
  }

  return `${provisional.addressId ?? 'no-id'}-${provisional.street ?? ''}-${
    provisional.city ?? ''
  }-${provisional.state ?? ''}-${provisional.neighborhood ?? ''}`
}

export function PostalCodeModalContent({
  onModalClose,
  inputValue,
  setInputValue,
  onSubmit,
  address,
  setProvisionalAddress,
  defaultAddressId,
  setDefaultAddressId,
  setPostalCode,
  setAddress,
}: PostalCodeModalContentProps) {
  const [isEditingAddress, setIsEditingAddress] = useState(false)
  const { orderForm, setOrderForm } = useCheckout()

  const handleEditAddressFromList = useCallback(
    (addressData: AddressData) => {
      setProvisionalAddress({ ...(addressData as Address) })
      setInputValue(checkHyphenInCEP(addressData.postalCode ?? ''))
      setIsEditingAddress(true)
    },
    [setInputValue, setProvisionalAddress]
  )

  const handleCancelAddressEdit = useCallback(() => {
    setInputValue('')
    setProvisionalAddress(null)
    setIsEditingAddress(false)
  }, [setInputValue, setProvisionalAddress])

  const handleStartNewAddressComposer = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      setIsEditingAddress(true)
    },
    []
  )

  const handleHeaderClose = useCallback(() => {
    onModalClose({
      shouldTrackClose: true,
      buttonName: 'Fechar',
      hasInputValue: inputValue.length > 0,
    })
  }, [onModalClose, inputValue])

  const handleAddressFormSavedSuccess = useCallback(
    (_data: AddressSchemaType, savedAddress?: AddressData) => {
      if (savedAddress) {
        syncContextAddressFromAddressData(
          savedAddress,
          setAddress,
          setPostalCode
        )
      } else {
        setPostalCode(checkHyphenInCEP(inputValue))
      }

      setInputValue('')
      setProvisionalAddress(null)
      setIsEditingAddress(false)

      onModalClose({
        shouldTrackClose: true,
        hasInputValue: inputValue.length > 0,
        buttonName: 'Salvar',
        skipTracking: true,
      })
    },
    [
      inputValue,
      onModalClose,
      setInputValue,
      setPostalCode,
      setProvisionalAddress,
      setAddress,
    ]
  )

  return (
    <div
      className={`px-4 flex h-full flex-col gap-3 ${
        isEditingAddress ? 'overflow-auto' : 'overflow-hidden'
      } font-medium md:px-6 md:pb-6`}
    >
      <PostalCodeModalHeader onClose={handleHeaderClose} />
      {!isEditingAddress ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <PostalCodeAddressList
            defaultAddressId={defaultAddressId}
            setDefaultAddressId={setDefaultAddressId}
            setPostalCode={setPostalCode}
            setAddress={setAddress}
            onEditAddress={handleEditAddressFromList}
          />
        </div>
      ) : null}
      {!isEditingAddress && (
        <>
          <button
            type="button"
            className="w-full flex items-center justify-center gap-2 h-11 border border-dashed border-postal-divider text-font-size-3 text-restructure-action font-semibold transition-colors focus:outline-none focus-visible:ring-2 hover:border-restructure-background-information-3"
            onClick={handleStartNewAddressComposer}
          >
            <PostalCodePlusIcon /> Adicionar novo endereço
          </button>
          <SectionDivider>
            <span className="text-postal-muted font-inter text-font-size-2 font-semibold tracking-[0.96px] uppercase">
              Ou informe um CEP
            </span>
          </SectionDivider>
          <PostalCodeInputForm
            inputValue={inputValue}
            setInputValue={setInputValue}
            onSubmit={onSubmit}
            setIsEditingAddress={setIsEditingAddress}
          />
          <a
            href={CORREIOS_CEP_FINDER_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="text-postal-muted font-normal text-[13px] leading-normal hover:underline cursor-pointer"
          >
            Não sei meu CEP
          </a>
        </>
      )}
      {isEditingAddress && (
        <>
          <div className="flex flex-col gap-1 pt-5 border-t border-postal-divider mb-2.5">
            <span className="text-font-size-2 font-semibold uppercase tracking-[0.96px] text-postal-muted">
              Confirme o endereço
            </span>
            <span className="text-[13px] leading-normal text-postal-muted">
              Revise os dados e complete o número do endereço.
            </span>
          </div>
          <PostalCodeInputForm
            inputValue={inputValue}
            setInputValue={setInputValue}
            onSubmit={onSubmit}
            showLabel
            hasAddress={!!address}
          />
          <AddressFormModal
            key={buildAddressFormDraftRemountKey(address)}
            data={address}
            onSubmit={handleAddressFormSavedSuccess}
            onCancel={handleCancelAddressEdit}
            orderForm={orderForm}
            setOrderForm={setOrderForm}
          />
        </>
      )}
    </div>
  )
}
